// Friendly status strip for the two failures users could only cure with F5:
// the API being unreachable for a moment (deploy restart, Wi-Fi blip) and an
// expired session. Driven by the events resilientFetch emits; when the
// connection comes back every active React Query screen refetches itself.
import React, { useEffect, useState } from 'react';
import { RefreshCw, WifiOff, Wifi, LogIn } from 'lucide-react';
import { queryClient } from '@/lib/queryClient';
import {
  CONNECTION_LOST_EVENT,
  CONNECTION_RESTORED_EVENT,
  SESSION_EXPIRED_EVENT,
} from '@/lib/resilientFetch';

type BannerState = 'hidden' | 'lost' | 'restored' | 'expired';

const RESTORED_VISIBLE_MS = 4000;

export const ConnectionBanner: React.FC = () => {
  const [state, setState] = useState<BannerState>('hidden');

  useEffect(() => {
    let hideTimer: ReturnType<typeof setTimeout> | undefined;

    const onLost = (): void => setState('lost');
    const onRestored = (): void => {
      setState('restored');
      void queryClient.refetchQueries({ type: 'active' });
      hideTimer = setTimeout(() => setState('hidden'), RESTORED_VISIBLE_MS);
    };
    const onExpired = (): void => setState('expired');
    const onOffline = (): void => setState('lost');
    const onOnline = (): void => onRestored();

    window.addEventListener(CONNECTION_LOST_EVENT, onLost);
    window.addEventListener(CONNECTION_RESTORED_EVENT, onRestored);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      window.removeEventListener(CONNECTION_LOST_EVENT, onLost);
      window.removeEventListener(CONNECTION_RESTORED_EVENT, onRestored);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  if (state === 'hidden') return null;

  const reload = (): void => window.location.reload();

  const palette: Record<Exclude<BannerState, 'hidden'>, string> = {
    lost: 'bg-amber-50 text-amber-900 border-amber-200',
    restored: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    expired: 'bg-red-50 text-red-900 border-red-200',
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-[1000] max-w-[calc(100vw-32px)] rounded-xl border px-4 py-3 shadow-lg text-sm flex items-center gap-3 ${palette[state]}`}
    >
      {state === 'lost' && (
        <>
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>Connexion au serveur interrompue. Nouvelle tentative en cours…</span>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 whitespace-nowrap"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser la page
          </button>
        </>
      )}
      {state === 'restored' && (
        <>
          <Wifi className="w-4 h-4 shrink-0" />
          <span>Connexion rétablie. Les données se rechargent.</span>
        </>
      )}
      {state === 'expired' && (
        <>
          <LogIn className="w-4 h-4 shrink-0" />
          <span>Votre session a expiré. Redirection vers la page de connexion…</span>
        </>
      )}
    </div>
  );
};

export default ConnectionBanner;
